import { CellEnum } from '@/enums/cell.enum'

import { ensureString } from '../type.util'
import { ExcelHeader } from './excel-header.constant'

type RawTrolleyRow = unknown[]
type RawTrolleySheet = RawTrolleyRow[]
export type ToolCell = Nullable<StrOrNum>
export type ToolRow = ToolCell[]
export type TrolleySheetData = Record<string, ToolRow[]>

export interface UploadedImageMeta {
  name: string
  fileKey?: Nullable<string>
  tempKey?: Nullable<string>
  sessionId?: Nullable<string>
}

export interface ImportedImage {
  name: string
  presignedUrl?: string
  fileKey?: string
  tempKey?: string
  sessionId?: string
}

export interface TrolleyListItem {
  name: string
  displayName: string
  data: ToolRow[]
  description?: Nullable<string>
}

export function isHeaderRow(row: ToolRow): boolean {
  if (!row || row.length === 0) return false
  const drawer = ensureString(row[CellEnum.Drawer]).toLowerCase()
  const pn = ensureString(row[CellEnum.Pn]).toLowerCase()
  const quantity = ensureString(row[CellEnum.Quantity]).toLowerCase()
  return (
    drawer === ExcelHeader.Drawer &&
    (pn === ExcelHeader.Pn || pn === ExcelHeader.PnAlt) &&
    quantity === ExcelHeader.Quantity
  )
}

export function isDescriptionRow(row: ToolRow): boolean {
  if (!row || row.length === 0) return false
  if (isHeaderRow(row)) return false
  const firstCell = ensureString(row[0])
  const hasOnlyFirstCell =
    row.length === 1 ||
    (row.length > 1 &&
      !row
        .slice(1)
        .some(cell => cell !== null && cell !== undefined && cell !== ''))
  return hasOnlyFirstCell && firstCell.length > 0
}

export function extractTrolleyDescription(
  sheetData: ToolRow[]
): Nullable<string> {
  if (!sheetData || sheetData.length === 0) return null
  const firstRow = sheetData[0]
  if (isDescriptionRow(firstRow)) {
    return ensureString(firstRow[0]) || null
  }
  return null
}

export function getDataRowsWithoutDescription(sheetData: ToolRow[]): ToolRow[] {
  if (!sheetData || sheetData.length === 0) return []
  const hasDescription = isDescriptionRow(sheetData[0])
  if (hasDescription) {
    return sheetData.slice(1)
  }
  return sheetData
}

export function buildImageLookup(
  images: UploadedImageMeta[] = []
): Record<string, UploadedImageMeta> {
  const map: Record<string, UploadedImageMeta> = {}
  images.forEach(img => {
    if (img.name) {
      const key = img.name.toLowerCase().trim()
      map[key] = img
      map[img.name] = img
    }
  })
  return map
}

export function buildImportedImageMap(
  images: ImportedImage[] = []
): Record<string, ImportedImage> {
  const map: Record<string, ImportedImage> = {}
  images.forEach(img => {
    if (img.name) {
      const key = img.name.toLowerCase().trim()
      map[key] = img
      map[img.name] = img
    }
  })
  return map
}

export function findImageByLabel(
  imageMap: Record<string, ImportedImage>,
  label: string
): ImportedImage | null {
  if (!label || typeof label !== 'string') return null

  let image = imageMap[label]
  if (image) return image

  const lowerKey = label.toLowerCase().trim()
  image = imageMap[lowerKey]
  if (image) return image

  const labelWithoutExt = label
    .replace(/\.[^/.]+$/, '')
    .toLowerCase()
    .trim()
  const foundImage = Object.values(imageMap).find(
    img =>
      img.name
        .replace(/\.[^/.]+$/, '')
        .toLowerCase()
        .trim() === labelWithoutExt
  )

  return foundImage || null
}

export function rowsToDrawerMap(
  rows: RawTrolleySheet
): Record<string, RawTrolleySheet> {
  return rows.reduce<Record<string, RawTrolleySheet>>((acc, row) => {
    const drawerName = ensureString(row[CellEnum.Drawer])
    if (!drawerName) return acc

    if (!acc[drawerName]) {
      acc[drawerName] = []
    }
    acc[drawerName].push(row)
    return acc
  }, {})
}

export function transformTrolleySheetData(
  data: TrolleySheetData
): TrolleyListItem[] {
  if (!data) return []
  return Object.entries(data).map(([name, sheetData]) => {
    const description = extractTrolleyDescription(sheetData)
    const dataWithoutDescription = getDataRowsWithoutDescription(sheetData)
    return {
      name,
      displayName: name,
      data: dataWithoutDescription,
      description,
    }
  })
}

export function extractDrawersFromSheet(sheetData: ToolRow[] | null): string[] {
  if (!sheetData || sheetData.length === 0) return []
  const dataRows = getDataRowsWithoutDescription(sheetData)
  const drawerSet = new Set<string>()
  dataRows.slice(1).forEach((row: ToolRow) => {
    const drawerName = row[CellEnum.Drawer]
    if (typeof drawerName === 'string' && drawerName) {
      drawerSet.add(drawerName)
    }
  })
  return Array.from(drawerSet)
}

export function countItemsByDrawer(
  sheetData: ToolRow[] | null
): Record<string, number> {
  if (!sheetData || sheetData.length === 0) return {}

  const dataRows = getDataRowsWithoutDescription(sheetData)
  const counts: Record<string, number> = {}
  dataRows.slice(1).forEach((row: ToolRow) => {
    const drawerName = row[CellEnum.Drawer] as Nullable<string>
    if (!drawerName) return
    counts[drawerName] = (counts[drawerName] || 0) + 1
  })

  return counts
}

export function filterItemsByDrawer(
  sheetData: ToolRow[] | null,
  drawerName: string | null
): ToolRow[] {
  if (!sheetData || !drawerName) return []
  const dataRows = getDataRowsWithoutDescription(sheetData)
  return dataRows
    .slice(1)
    .filter((row: ToolRow) => row[CellEnum.Drawer] === drawerName)
}

export function filterItemsBySearch(
  items: ToolRow[],
  searchTerm: string
): ToolRow[] {
  if (!searchTerm.trim()) return items

  const searchLower = searchTerm.toLowerCase()
  return items.filter((row: ToolRow) => {
    return (
      (row[CellEnum.Pn] &&
        row[CellEnum.Pn].toString().toLowerCase().includes(searchLower)) ||
      (row[CellEnum.ChineseName] &&
        row[CellEnum.ChineseName]
          .toString()
          .toLowerCase()
          .includes(searchLower)) ||
      (row[CellEnum.Description] &&
        row[CellEnum.Description]
          .toString()
          .toLowerCase()
          .includes(searchLower))
    )
  })
}

export function getTrolleyItemCount(sheetData: ToolRow[]): number {
  if (!sheetData || sheetData.length === 0) return 0
  const dataRows = getDataRowsWithoutDescription(sheetData)
  return Math.max(0, dataRows.length - 1)
}

export function validateTrolleyDataIntegrity(data: TrolleySheetData): boolean {
  if (!data || Object.keys(data).length === 0) return false

  return Object.values(data).every(sheetData => {
    const dataRows = getDataRowsWithoutDescription(sheetData)

    if (dataRows.length === 0) return true

    const headerRow = dataRows[0]
    if (!headerRow) return false

    const pn = ensureString(headerRow[CellEnum.Pn]).toLowerCase().trim()
    const chineseName = ensureString(headerRow[CellEnum.ChineseName])
      .toLowerCase()
      .trim()
    const description = ensureString(headerRow[CellEnum.Description])
      .toLowerCase()
      .trim()
    const quantity = ensureString(headerRow[CellEnum.Quantity])
      .toLowerCase()
      .trim()

    const isPnValid = pn === ExcelHeader.Pn || pn === ExcelHeader.PnAlt
    const isQuantityValid = quantity === ExcelHeader.Quantity
    const isDescriptionValid = description.includes('description')
    const isChineseNameValid =
      chineseName.includes('chinese') && chineseName.includes('name')

    return (
      isPnValid && isQuantityValid && isDescriptionValid && isChineseNameValid
    )
  })
}

export function validateTrolleyDrawerLimit(data: TrolleySheetData): boolean {
  if (!data) return true

  return Object.values(data).every(sheetData => {
    const drawers = extractDrawersFromSheet(sheetData)
    return drawers.length <= 100
  })
}
